import React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PATH } from 'src/features/navigation/utils/constants';
import { PaymentsListDTO, useListPayments } from 'src/features/payment/api/useListPayments';
import PaymentSidePanel from 'src/features/payment/components/PaymentSidePanel';
import {
    PAYMENTS_SIDE_PANEL,
    paymentSourceTranslationMap,
    paymentTypeBadgeOptions,
} from 'src/features/payment/utils/constants';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { Payment, PAYMENT_ITEM_TYPE, PAYMENT_SERVICE_TYPE } from 'src/features/payment/utils/types';
import { CellSidePanelActions } from 'src/features/visitor/components/cell/CellSidePanelActions';
import SidePanel from 'src/registry/components/sidePanel/SidePanel';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import { useSingleRowSelection } from 'src/registry/hooks/useSingleRowSelection';
import useSorting from 'src/registry/hooks/useSorting';
import CellLinkWithIcon from 'src/features/membership/components/cell/CellLinkWIthIcon';
import { useParking } from 'src/providers/ParkingProvider';
import { PERMISSION } from 'src/features/auth';
import useHasPermissions from 'src/hooks/useHasPermissions';

type PaymentsTableProps = {
    itemId?: string;
    visitorId?: string;
    entityId?: string;
    hasExtraColumns?: boolean;
};

const PaymentsTable = ({ itemId, visitorId, entityId, hasExtraColumns = false }: PaymentsTableProps) => {
    const { formatMessage, formatNumber } = useIntl();
    const { querySortingOptions, ...sorting } = useSorting({ initialSortingState: [{ id: 'date', desc: true }] });
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { queryPaginationOptions, ...pagination } = usePagination();

    const { parkingId } = useParking();

    const paymentsQuery: PaymentsListDTO = {
        parkingId: parkingId,
    };
    if (itemId) {
        paymentsQuery.item_id = itemId;
    } else if (entityId) {
        paymentsQuery.payload_details = JSON.stringify({ entity_id: entityId });
        paymentsQuery.payload_details = JSON.stringify({ customer: entityId });
    } else if (visitorId) {
        paymentsQuery.payload_details = JSON.stringify({ visitor_id: visitorId });
    }

    const {
        data: listPayments,
        isLoading,
        isError,
    } = useListPayments({
        ...paymentsQuery,
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...queryPaginationOptions,
    });

    const { rowSelectionState, clearSelection, setRowSelectionState } = useSingleRowSelection();

    const singleRowSelection = React.useCallback(
        (rowId: string) => {
            setRowSelectionState({ [rowId]: true });
        },
        [setRowSelectionState],
    );

    const accessActiveStays = useHasPermissions([PERMISSION.ACCESS_ACTIVE_STAYS]);
    const accessPastStays = useHasPermissions([PERMISSION.ACCESS_PAST_STAYS]);
    const accessVisitors = useHasPermissions([PERMISSION.ACCESS_VISITORS]);

    const paymentId = React.useMemo(() => {
        const firstSelected = Object.keys(rowSelectionState)[0];
        if (!listPayments || !firstSelected) return '';
        return listPayments.results.find((row) => row.id === firstSelected)?.id;
    }, [listPayments, rowSelectionState]);

    const columnHelper = createColumnHelper<Payment>();
    const columns = [
        columnHelper.accessor('date', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(paymentsMessages.timestamp),
            },
        }),
        columnHelper.accessor('amount', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return formatNumber(row.original.amount / 100, {
                    style: 'currency',
                    currency: row.original.currency,
                });
            },
            meta: {
                title: formatMessage(paymentsMessages.amount),
            },
        }),
        columnHelper.accessor('payment_payload_details.service_type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const serviceType = row.original?.payment_payload_details?.service_type;
                const isParkingServiceType = serviceType === PAYMENT_SERVICE_TYPE.PARKING;

                return isParkingServiceType ? formatMessage(paymentsMessages.parking) : serviceType;
            },
            meta: {
                title: formatMessage(paymentsMessages.serviceType),
            },
        }),
        ...(hasExtraColumns
            ? [
                  columnHelper.accessor('payment_payload_details.item_display', {
                      header: DataTableColumnHeader,
                      enableHiding: true,
                      cell: ({ row }) => {
                          const serviceId = row.original.payment_payload_details.item_id;
                          const service = row.original.payment_payload_details.item_display;
                          const stayType: PAYMENT_ITEM_TYPE = row.original.payment_payload_details.item_type;

                          const urlLink =
                              stayType === PAYMENT_ITEM_TYPE.ACTIVE_STAY
                                  ? `/${PATH.VISITORS}/active-stays/dashboard/${serviceId}`
                                  : stayType === PAYMENT_ITEM_TYPE.PAST_STAY
                                    ? `/${PATH.VISITORS}/past-stays/dashboard/${serviceId}`
                                    : undefined; //TODO: eventually this will be a link to membership dashboard

                          const hasPermissions =
                              stayType === PAYMENT_ITEM_TYPE.ACTIVE_STAY ? accessActiveStays : accessPastStays;

                          return serviceId && urlLink ? (
                              <CellLinkWithIcon
                                  toPath={urlLink}
                                  linkTitle={`${formatMessage(paymentsMessages.stay)} #${service}`}
                                  hasPermissions={hasPermissions}
                              />
                          ) : (
                              '-'
                          );
                      },
                      meta: {
                          title: formatMessage(paymentsMessages.serviceColumnTitle),
                      },
                  }),
                  columnHelper.accessor('payment_payload_details.visitor_display', {
                      header: DataTableColumnHeader,
                      enableHiding: true,
                      cell: ({ row }) => {
                          const visitorId = row.original.payment_payload_details.visitor_id;
                          const visitor = row.original.payment_payload_details.visitor_display;

                          return visitorId ? (
                              <CellLinkWithIcon
                                  toPath={`/${PATH.VISITORS}/visitors/${visitorId}`}
                                  linkTitle={visitor}
                                  hasPermissions={accessVisitors}
                              />
                          ) : (
                              '-'
                          );
                      },
                      meta: {
                          title: formatMessage(paymentsMessages.visitor),
                      },
                  }),
              ]
            : []),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(paymentsMessages.paymentType),
                badgeOptions: paymentTypeBadgeOptions,
            },
        }),
        columnHelper.accessor('payment_payload_details.receipt_number', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const receiptNumber = row.original.payment_payload_details?.receipt_number;
                return <span className="font-medium">{receiptNumber ? `#${receiptNumber}` : '-'}</span>;
            },
            meta: {
                title: formatMessage(paymentsMessages.receiptNumber),
            },
        }),
        columnHelper.accessor('payment_payload_details.payment_source_display', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const paymentSource = row.original.payment_payload_details?.payment_source_display;
                const paymentSourceType = row.original.payment_payload_details?.payment_source_type;
                return (
                    <div className="flex flex-col">
                        <span className="font-medium">{paymentSource}</span>
                        {paymentSourceType && (
                            <span className="whitespace-nowrap">{`${formatMessage(paymentSourceTranslationMap[paymentSourceType])}`}</span>
                        )}
                    </div>
                );
            },
            meta: {
                title: formatMessage(paymentsMessages.paidOn),
            },
        }),
        columnHelper.display({
            id: 'actions',
            cell: (props) => (
                <CellSidePanelActions
                    {...props}
                    sidePanelName={PAYMENTS_SIDE_PANEL}
                    singleRowSelection={singleRowSelection}
                />
            ),
        }),
    ];

    return (
        <>
            <DataTable
                isLoading={isLoading}
                isError={isError}
                columns={columns as any}
                data={listPayments}
                pagination={pagination}
                sorting={sorting}
                search={search}
                filters={filters}
                enableSearchParams={false}
                enableLocalFilters={false}
                semantics={{
                    title: formatMessage(paymentsMessages.payments),
                    entryName: formatMessage(paymentsMessages.payment).toLowerCase(),
                    entryNames: formatMessage(paymentsMessages.payments).toLowerCase(),
                }}
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
            />
            <SidePanel onClose={clearSelection} name={PAYMENTS_SIDE_PANEL}>
                <PaymentSidePanel paymentId={paymentId} accessFromBillingModule={hasExtraColumns} />
            </SidePanel>
        </>
    );
};

export default PaymentsTable;
